import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

import { IsfV1Model } from '@data-layer/models/isf-v1';
import { DocumentCreateModel, DocumentModel } from '@data-layer/models/documents';
import {
  enumDocumentRelatedEntityType,
  enumDocumentRootEntityType,
  enumDocumentState,
  enumDocumentType,
} from '@data-layer/enums/documents';
import { DocumentsService } from '@services/documents.service';
import { switchMap, take, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';

@Component({
  selector: 'isf-attach-documents',
  templateUrl: './isf-attach-documents.component.html',
  styleUrls: ['./isf-attach-documents.component.scss'],
})
export class IsfAttachDocumentsComponent implements OnChanges {
  @Input() public isf: IsfV1Model;
  @Input() public openDocuments: boolean = false;
  @Input() public isCanUploadDocuments: boolean = false;
  @Input() public isCanRemoveDocuments: boolean = false;
  @Input() public useCompanyId: number;
  @Input() public isHideDownloadAll: boolean;

  @Output() public openDocumentsChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  public attachedDocuments: DocumentModel[] = [];
  public enumDocumentType: typeof enumDocumentType = enumDocumentType;
  public createDocumentTemplate: DocumentCreateModel;

  public includesTypes = [
    enumDocumentType.ARRIVAL_NOTICE,
    enumDocumentType.ISF_FORM,
    enumDocumentType.BILL_OF_LADING,
    enumDocumentType.UNKNOWN,
  ];

  constructor(private documentsService: DocumentsService) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.isf && this.isf) {
      this.loadDocuments(true).pipe(take(1)).subscribe();
    }
    this.createDocumentTemplate = {
      type: null,
      relatedEntityId: this.isf.id,
      relatedEntityType: enumDocumentRelatedEntityType.ISF,
      rootEntityType: enumDocumentRootEntityType.ISF,
      rootEntityId: this.isf.id,
      fileName: null,
    };
  }

  public onUploadDocument(): (document: DocumentCreateModel, file: File) => Observable<any> {
    return this.uploadDocument.bind(this);
  }

  public onRemoveDocument(): (documentId: number) => Observable<any> {
    return this.removeDocument.bind(this);
  }

  public onDocumentStateUpdate(documentId: number, state: enumDocumentState) {
    const docTypesToSkip: enumDocumentType[] = [enumDocumentType.FILE_PREVIEW];
    this.attachedDocuments = this.attachedDocuments
      .filter((doc) => !docTypesToSkip.includes(doc.type))
      .map((document) => (document.id === documentId ? { ...document, state } : document));
  }

  public onDownloadAllFiles(): void {
    const docIds: number[] = this.attachedFilteredDocuments.map((item: DocumentModel) => item.id);

    this.documentsService
      .getArchive(this.isf.referenceNumber, docIds)
      .pipe(take(1))
      .subscribe((file: Blob) => {
        this.documentsService.downloadBlobFile(file, this.documentsService.formatZipFileName(this.isf.referenceNumber));
      });
  }

  public get attachedFilteredDocuments(): DocumentModel[] {
    return this.attachedDocuments.filter((item: DocumentModel) => !!item.id);
  }

  public get isUploadedAnyFiles(): boolean {
    return !!this.attachedFilteredDocuments.length;
  }

  private removeDocument(documentId: number): Observable<any> {
    return this.documentsService.removeDocument(documentId).pipe(switchMap(() => this.loadDocuments(false)));
  }

  private uploadDocument(document: DocumentCreateModel, file: File): Observable<any> {
    return this.documentsService.uploadDocument(document, file, this.useCompanyId).pipe(switchMap(() => this.loadDocuments(false)));
  }

  private loadDocuments(isShowLoader: boolean) {
    const docTypesToSkip: enumDocumentType[] = [enumDocumentType.FILE_PREVIEW];

    return this.documentsService.getIsfDocuments(this.isf.id, isShowLoader).pipe(
      tap((documents) => {
        this.attachedDocuments = documents.filter((doc) => !docTypesToSkip.includes(doc.type));
        if (this.attachedDocuments.length === 0) {
          this.attachedDocuments.push({
            type: enumDocumentType.UNKNOWN,
            relatedEntityId: this.isf.id,
            relatedEntityType: enumDocumentRelatedEntityType.ISF,
            rootEntityType: enumDocumentRootEntityType.ISF,
            rootEntityId: this.isf.id,
          } as DocumentModel);
        } else {
          this.openDocumentsChange.emit(true);
        }
      }),
    );
  }
}
